import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Link } from 'react-router-dom'
import {
	getEmailTemplates,
	createEmailTemplate,
	updateEmailTemplate,
	deleteEmailTemplate,
} from '../../services/httpService'
import { useDarkMode } from '../../context/DarkModeContext'
import { DocumentTextIcon, EyeIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import TemplateModal from './TemplateModal'
import TemplatePreviewModal from './TemplatePreviewModal'
import ActionsMenu from './ActionsMenu'
import LimitReachedModal from './LimitReachedModal'
import { emailSettingsStyles } from './emailSettingsStyles'

const serverError = (error, fallback) => ({
	message: error.response?.data?.message || fallback,
	needsUpgrade: error.response?.status === 402,
})

const templatesWord = count => (count === 1 ? 'template' : 'templates')
const templateLimitMessage = limit =>
	`You have reached your plan's limit of ${limit} ${templatesWord(limit)}. Please delete an existing template before adding a new one.`

// The page's header owns the "+ New template" button and calls
// startNewTemplate through the ref.
const EmailTemplates = forwardRef((props, ref) => {
	const { isDarkMode } = useDarkMode()
	const isLight = !isDarkMode
	const styles = emailSettingsStyles(isLight)
	const [templates, setTemplates] = useState([])
	const [allowance, setAllowance] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [deletingTemplateId, setDeletingTemplateId] = useState(null)
	const [isLimitModalOpen, setIsLimitModalOpen] = useState(false)
	const [previewing, setPreviewing] = useState(null)
	// null: closed, 'new': creating, otherwise the template being edited.
	const [editing, setEditing] = useState(null)

	const fetchTemplates = async () => {
		try {
			const response = await getEmailTemplates()
			setTemplates(response.data.templates || [])
			setAllowance({ limit: response.data.templateLimit, inUse: response.data.templatesInUse })
		} catch (err) {
			setError(serverError(err, 'Could not load your templates.'))
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchTemplates()
	}, [])

	// The limit is null when the server couldn't check the plan; the server still enforces it on save.
	const isAtLimit = allowance?.limit != null && allowance.inUse >= allowance.limit

	const handleNewTemplate = () => {
		if (isAtLimit) {
			setIsLimitModalOpen(true)
			return
		}
		setError(null)
		setEditing('new')
	}

	useImperativeHandle(ref, () => ({ startNewTemplate: handleNewTemplate }))

	const handleSave = async template => {
		try {
			if (editing === 'new') await createEmailTemplate(template)
			else await updateEmailTemplate(editing._id, template)
		} catch (err) {
			throw serverError(err, 'Saving the template failed. Please try again.')
		}
		await fetchTemplates()
	}

	const handleDelete = async template => {
		if (!window.confirm(`Delete the template "${template.name}"?`)) return
		setDeletingTemplateId(template._id)
		setError(null)
		try {
			await deleteEmailTemplate(template._id)
			await fetchTemplates()
		} catch (err) {
			setError(serverError(err, 'Deleting the template failed. Please try again.'))
		} finally {
			setDeletingTemplateId(null)
		}
	}

	// Same list styling as the mailboxes on the Email accounts tab.
	const listClass = `rounded-lg border divide-y ${isLight ? 'border-gray-200 divide-gray-200' : 'border-[#374151] divide-[#374151]'}`

	if (loading) {
		return (
			<div className={listClass}>
				{[1, 2].map(index => (
					<div key={index} className="px-4 py-3 animate-pulse">
						<div className={`h-4 rounded w-40 mb-2 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`} />
						<div className={`h-3 rounded w-64 ${isLight ? 'bg-gray-200' : 'bg-gray-600'}`} />
					</div>
				))}
			</div>
		)
	}

	return (
		<>
			{error && (
				<div role="alert" className={`${styles.errorBox} mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2`}>
					<span>{error.message}</span>
					{error.needsUpgrade && (
						<Link to="/settings/subscription" className="font-semibold underline whitespace-nowrap">
							See plans
						</Link>
					)}
				</div>
			)}


			{templates.length === 0 ? (
				<div className={`${listClass} px-4 py-6 text-center text-sm ${styles.muted}`}>No templates yet.</div>
			) : (
				<ul className={listClass}>
					{templates.map(template => (
						<li
							key={template._id}
							className={`px-4 py-3 flex items-center gap-3 ${deletingTemplateId === template._id ? 'opacity-50 pointer-events-none' : ''}`}
						>
							<div className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center ${isLight ? 'bg-gray-200 text-gray-600' : 'bg-[#374151] text-gray-300'}`}>
								<DocumentTextIcon className="w-4 h-4" />
							</div>
							<div className="min-w-0 flex-1">
								<div className={`text-sm font-medium truncate ${styles.text}`}>{template.name}</div>
								<div className={`text-xs truncate ${styles.muted}`}>{template.subject}</div>
							</div>
							<div className="flex items-center gap-1 shrink-0">
								<button type="button" onClick={() => setPreviewing(template)} aria-label="Preview template" title="Preview" className={styles.iconButton}>
									<EyeIcon className="w-4 h-4" />
								</button>
								<button type="button" onClick={() => setEditing(template)} aria-label="Edit template" title="Edit" className={styles.iconButton}>
									<PencilSquareIcon className="w-4 h-4" />
								</button>
								<ActionsMenu
									label="Template actions"
									actions={[{ label: 'Delete template', onClick: () => handleDelete(template), danger: true }]}
								/>
							</div>
						</li>
					))}
				</ul>
			)}

			{previewing && (
				<TemplatePreviewModal
					template={previewing}
					onEdit={() => {
						setEditing(previewing)
						setPreviewing(null)
					}}
					onClose={() => setPreviewing(null)}
				/>
			)}

			{isLimitModalOpen && (
				<LimitReachedModal
					title="Maximum Templates Reached"
					message={templateLimitMessage(allowance.limit)}
					onClose={() => setIsLimitModalOpen(false)}
				/>
			)}

			{editing && (
				<TemplateModal
					template={editing === 'new' ? null : editing}
					onSave={handleSave}
					onClose={() => setEditing(null)}
				/>
			)}
		</>
	)
})

export default EmailTemplates
